import { AppMenu } from "../components/AppMenu";
import { ProfileButton } from "@/features/auth";
import { useAdminTitleRefresh } from "@/features/auth";
import { ChevronLeft } from "lucide-react";
import { Link, Navigate } from "react-router-dom";

import {
  PORTAL_CHOOSER_PATH,
  resolvePortals,
  useAuth,
  type PortalDescriptor,
} from "@/features/auth";
import { CardContainer } from "@/shared/components/CardContainer";
import { MobileHeader } from "@/shared/components/MobileHeader";
import { ResponsiveViewWrapper } from "@/shared/components/ResponsiveViewWrapper";

/**
 * Portal chooser for a person who holds more than one portal role in the active school.
 *
 * A person can be admin AND teacher of the same school. Before a admin portal existed,
 * the app silently landed a multi-role account on one portal, which left the other role's screens
 * reachable only by guessing a URL. This screen makes every granted portal an explicit, visible
 * choice instead.
 *
 * It also renders when a single-role user arrives directly at the URL: the chooser is a valid
 * page, and bouncing them straight back to where they came from would be a redirect loop.
 */
export function PortalChooserPage() {
  useAdminTitleRefresh();
  const { isAuthenticated, user, roles, activeTenant } = useAuth();

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  const portals = resolvePortals(roles, activeTenant?.adminTitle);

  return (
    <ResponsiveViewWrapper
      maxMobileWidth={430}
      className="bg-frame-surface"
      scrollable={false}
      aria-label="انتخاب پرتال"
    >
      <MobileHeader
        leadingAction={<ProfileButton />}
        title="کایرسافت"
        subtitle="انتخاب پرتال"
        showBack={false}
        trailingAction={<AppMenu />}
      />

      <div
        className="flex-1 space-y-4 overflow-x-hidden overflow-y-auto p-4 select-none"
        dir="rtl"
      >
        <div className="space-y-1 pt-1 text-center">
          <h2 className="text-slate-dark text-sm font-bold">
            خوش آمدید، {user.displayName}
          </h2>
          <p className="text-slate-muted text-[11px] font-medium">
            {activeTenant?.tenantName ?? "مدرسه"} — پرتال مورد نظر خود را انتخاب
            کنید
          </p>
        </div>

        {portals.length === 0 ? (
          <CardContainer variant="soft" className="space-y-2 py-8 text-center">
            <p className="text-slate-dark text-xs font-semibold">
              برای نقش فعلی شما پرتالی در دسترس نیست.
            </p>
            <p className="text-slate-muted text-[11px]">
              مدرسه فعال دیگری را انتخاب کنید یا با پشتیبانی تماس بگیرید.
            </p>
          </CardContainer>
        ) : (
          <ul className="space-y-2.5">
            {portals.map((portal) => (
              <li key={portal.role}>
                <PortalChoiceCard portal={portal} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </ResponsiveViewWrapper>
  );
}

/** One selectable portal, with its name and what it contains. */
function PortalChoiceCard({ portal }: { portal: PortalDescriptor }) {
  return (
    <Link
      to={portal.home}
      aria-label={`ورود به ${portal.title}`}
      className="focus-visible:outline-brand rounded-card block focus-visible:outline-2"
    >
      <CardContainer
        variant="standard"
        className="hover:border-brand/40 flex items-center gap-3 transition-colors"
      >
        <div className="min-w-0 flex-1">
          <p className="text-slate-dark text-sm font-bold">{portal.title}</p>
          <p className="text-slate-muted mt-0.5 text-[11px] font-medium">
            {portal.description}
          </p>
        </div>
        <ChevronLeft
          className="text-slate-placeholder h-4 w-4 shrink-0"
          aria-hidden="true"
        />
      </CardContainer>
    </Link>
  );
}

/** Exported for the router to reference the chooser's path from one place. */
export { PORTAL_CHOOSER_PATH };
